import React, { useEffect, useState } from 'react';
import { Plus, Minus } from 'lucide-react';

// QtyControl — direct-input quantity selector with +/- buttons and optional
// quick-add chips. Drop-in for the previous "[-] N [+]" pill.
//
// Props
//   value          : current qty (number)
//   onChange       : (newQty:number) => void   — fires for ANY change source
//                                                 (typing, +/-, quick-add).
//                                                 Pass 0 to indicate the line
//                                                 should be removed.
//   min            : minimum order qty (default 1)
//   max            : optional upper bound
//   step           : +/- button increment (default 1)
//   quickAdds      : array of increments to render as chip buttons, e.g. [10,25,50,100]
//                    Pass [] or omit to hide.
//   testidPrefix   : data-testid prefix used by the three sub-controls
//                    (`-input`, `-minus`, `-plus`, `-quick-<n>`).
//   compact        : when true, render in a tighter pill layout (catalogue cards)
//
// IMPORTANT: This component only manages the LOCAL display state and emits
// `onChange`. All cart/inventory/order business logic stays in the parent.
export default function QtyControl({
  value,
  onChange,
  min = 1,
  max,
  step = 1,
  quickAdds = [],
  testidPrefix = 'qty',
  compact = false,
}) {
  // Local string mirror so the user can clear the field mid-typing without
  // the parent immediately snapping it back to min.
  const [draft, setDraft] = useState(String(value ?? min));
  useEffect(() => {
    setDraft(String(value ?? min));
  }, [value, min]);

  const clamp = (n) => {
    let v = Number.isFinite(n) ? Math.floor(n) : min;
    if (v < 0) v = 0;
    if (max !== undefined && v > max) v = max;
    return v;
  };

  const commit = (raw) => {
    if (raw === '' || raw === '0') {
      // Removing the line: fire 0 and let the parent decide.
      onChange(0);
      return;
    }
    const n = clamp(Number(raw));
    // Below-min on blur → 0 (parent will remove line). Lets users type "0".
    if (n > 0 && n < min) {
      onChange(0);
      return;
    }
    onChange(n);
  };

  const handleStep = (delta) => {
    const current = Number(draft) || 0;
    let next = clamp(current + delta);
    if (delta > 0 && current === 0) next = Math.max(next, min);
    if (delta < 0 && next > 0 && next < min) next = 0;
    setDraft(String(next));
    onChange(next);
  };

  const handleQuickAdd = (inc) => {
    const current = Number(draft) || 0;
    const next = clamp((current === 0 ? Math.max(min, inc) : current + inc));
    setDraft(String(next));
    onChange(next);
  };

  // Allow only digits while typing — strip everything else, mid-type "" is OK.
  const handleType = (e) => {
    const cleaned = e.target.value.replace(/[^\d]/g, '');
    setDraft(cleaned);
  };

  const pillCls = compact
    ? 'inline-flex items-center gap-1 bg-orange-50 rounded-full px-1 py-1'
    : 'inline-flex items-center gap-2 bg-orange-50 rounded-full px-1.5 py-1';
  const btnCls = compact
    ? 'bg-orange-700 hover:bg-orange-800 text-white rounded-full w-7 h-7 flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed'
    : 'bg-orange-700 hover:bg-orange-800 text-white rounded-full w-8 h-8 flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed';
  const inputCls = compact
    ? 'w-12 h-7 text-center bg-white border border-orange-200 rounded-md text-sm font-bold text-orange-900 focus:outline-none focus:ring-2 focus:ring-orange-400'
    : 'w-16 h-8 text-center bg-white border border-orange-200 rounded-md text-sm font-bold text-orange-900 focus:outline-none focus:ring-2 focus:ring-orange-400';

  const atMin = (Number(draft) || 0) <= min;
  const atMax = max !== undefined && (Number(draft) || 0) >= max;

  return (
    <div className="flex flex-col items-end gap-1.5">
      <div className={pillCls}>
        <button
          type="button"
          onClick={() => handleStep(-step)}
          disabled={atMin && Number(draft) === 0}
          aria-label="Decrease quantity"
          className={btnCls}
          data-testid={`${testidPrefix}-minus`}
        >
          <Minus className="w-3 h-3" />
        </button>
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          value={draft}
          onChange={handleType}
          onFocus={(e) => e.target.select()}
          onBlur={(e) => commit(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
          }}
          className={inputCls}
          aria-label="Order quantity"
          data-testid={`${testidPrefix}-input`}
        />
        <button
          type="button"
          onClick={() => handleStep(step)}
          disabled={atMax}
          aria-label="Increase quantity"
          className={btnCls}
          data-testid={`${testidPrefix}-plus`}
        >
          <Plus className="w-3 h-3" />
        </button>
      </div>

      {quickAdds.length > 0 && (
        <div
          className="flex items-center gap-1 flex-wrap justify-end"
          data-testid={`${testidPrefix}-quick-row`}
        >
          {quickAdds.map((inc) => (
            <button
              key={inc}
              type="button"
              onClick={() => handleQuickAdd(inc)}
              className="text-[11px] font-semibold text-orange-700 bg-orange-50 hover:bg-orange-100 border border-orange-200 rounded-full px-2 py-0.5 transition-colors"
              data-testid={`${testidPrefix}-quick-${inc}`}
            >
              +{inc}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
